/**
 * Dictionnaire FR/EN du header + footer global.
 *
 * Les `href` restent en FR (canoniques d'origine) ; ils sont localisés au
 * rendu via localeNavHref(href, locale). Seuls les libellés changent selon la
 * locale détectée du chemin courant. FR = textes d'origine à l'identique.
 */
import { type I18nText } from "@/config/localeText";

type T = I18nText;

export type NavServiceItem = {
  href: string;
  title: T;
  description: T;
};

export type NavLink = {
  href: string;
  label: T;
};

export const HEADER_NAV = {
  triggerChauffeur: {
    fr: "Chauffeur Privé",
    en: "Private Chauffeur",
    ar: "سائق خاص",
  } as T,
  triggerLocation: {
    fr: "Location de Véhicules",
    en: "Car Rental",
    ar: "تأجير السيارات",
  } as T,
  triggerPartners: {
    fr: "Partenaires",
    en: "Partners",
    ar: "الشركاء",
  } as T,
  about: { fr: "À propos", en: "About", ar: "من نحن" } as T,
  contact: { fr: "Contact", en: "Contact", ar: "اتصل بنا" } as T,
  openMenu: { fr: "Ouvrir le menu", en: "Open menu", ar: "فتح القائمة" } as T,
  closeMenu: {
    fr: "Fermer le menu",
    en: "Close menu",
    ar: "إغلاق القائمة",
  } as T,
  mainBadge: { fr: "Principal", en: "Main", ar: "رئيسي" } as T,
  secondaryBadge: { fr: "Secondaire", en: "Secondary", ar: "ثانوي" } as T,
  cta: {
    book: {
      fr: "Réserver un chauffeur",
      en: "Book a chauffeur",
      ar: "احجز سائقًا خاصًا",
    } as T,
    rent: {
      fr: "Louer un véhicule",
      en: "Rent a vehicle",
      ar: "استأجر سيارة",
    } as T,
    bookShort: { fr: "Réserver", en: "Book", ar: "احجز" } as T,
    rentShort: { fr: "Louer", en: "Rent", ar: "استأجر" } as T,
  },
  driverServices: [
    {
      href: "/chauffeur/services",
      title: {
        fr: "Service Chauffeur Privé",
        en: "Private Chauffeur Service",
        ar: "خدمة السائق الخاص",
      },
      description: {
        fr: "Un service de chauffeur privé haut de gamme pour tous vos déplacements",
        en: "A premium private chauffeur service for all your journeys",
        ar: "خدمة سائق خاص راقية لجميع تنقلاتكم",
      },
    },
    {
      href: "/chauffeur/aeroport",
      title: {
        fr: "Transferts Aéroport",
        en: "Airport Transfers",
        ar: "نقل المطار",
      },
      description: {
        fr: "Service de transfert aéroport professionnel et ponctuel",
        en: "Professional, punctual airport transfer service",
        ar: "خدمة نقل من وإلى المطار باحترافية ودقة في المواعيد",
      },
    },
    {
      href: "/chauffeur/evenements",
      title: {
        fr: "Événements Spéciaux",
        en: "Special Events",
        ar: "المناسبات الخاصة",
      },
      description: {
        fr: "Mariages, cérémonies et événements d'entreprise",
        en: "Weddings, ceremonies and corporate events",
        ar: "حفلات الزفاف والمراسم ومناسبات الشركات",
      },
    },
    {
      href: "/chauffeur/entreprise",
      title: {
        fr: "Service Entreprise",
        en: "Business Service",
        ar: "خدمة الشركات",
      },
      description: {
        fr: "Solutions de transport dédiées aux professionnels",
        en: "Dedicated transport solutions for professionals",
        ar: "حلول نقل مخصّصة للمهنيين ورجال الأعمال",
      },
    },
  ] as NavServiceItem[],
  rentalServices: [
    {
      href: "/location/vehicules#catalogue-location",
      title: {
        fr: "Location de Véhicules",
        en: "Vehicle Rental",
        ar: "تأجير السيارات",
      },
      description: {
        fr: "Large gamme de véhicules haut de gamme disponibles",
        en: "A wide range of premium vehicles available",
        ar: "تشكيلة واسعة من السيارات الفاخرة المتاحة",
      },
    },
    {
      // Ajoutee le 20/09/2026. Le groupe de recherches « voiture avec
      // chauffeur » pese 4 743 impressions pour 2 clics sur 12 mois
      // (Search Console) et n'avait aucune page. Un lien de menu evite la
      // page orpheline et lui donne un lien site-wide.
      href: "/location/avec-chauffeur",
      title: {
        fr: "Avec ou sans chauffeur",
        en: "With or without a driver",
        ar: "مع سائق أو بدونه",
      },
      description: {
        fr: "Les deux formules comparees : au volant ou conduit, tarifs affiches.",
        en: "Both options compared: self-drive or chauffeured, rates shown.",
        ar: "مقارنة بين الصيغتين: بقيادتك أو مع سائق، مع الأسعار.",
      },
    },
    {
      href: "/location/longue-duree",
      title: {
        fr: "Location Longue Durée",
        en: "Long-Term Rental",
        ar: "التأجير طويل الأمد",
      },
      description: {
        fr: "Solutions de location flexibles pour les particuliers et entreprises",
        en: "Flexible rental solutions for individuals and businesses",
        ar: "حلول تأجير مرنة للأفراد والشركات",
      },
    },
    {
      href: "/location/prestige",
      title: {
        fr: "Véhicules Prestige",
        en: "Prestige Vehicles",
        ar: "سيارات بريستيج",
      },
      description: {
        fr: "Collection exclusive de véhicules de luxe",
        en: "Exclusive collection of luxury vehicles",
        ar: "مجموعة حصرية من سيارات الفخامة",
      },
    },
    {
      href: "/location/offres",
      title: {
        fr: "Offres Spéciales",
        en: "Special Offers",
        ar: "عروض خاصة",
      },
      description: {
        fr: "Découvrez nos offres et promotions en cours",
        en: "Discover our current offers and promotions",
        ar: "اكتشفوا عروضنا وتخفيضاتنا الحالية",
      },
    },
  ] as NavServiceItem[],
  // Pilier B2B / pro : pages partenaires à forte valeur (apporteurs d'affaires,
  // conciergeries, hôtels, yachting, mariages, agences). Distinct des 2 piliers
  // « services » pour rappeler que KS Rent Services adresse aussi les pros.
  partnerServices: [
    {
      href: "/chauffeur/partenaires",
      title: {
        fr: "Programme partenaires",
        en: "Partner program",
        ar: "برنامج الشركاء",
      },
      description: {
        fr: "Devenez apporteur d'affaires et percevez une commission sur chaque client confié.",
        en: "Become a business referrer and earn a commission on every client you refer.",
        ar: "كن وسيط أعمال واحصل على عمولة عن كل عميل تُحيله إلينا.",
      },
    },
    {
      href: "/chauffeur/partenaires/conciergerie",
      title: {
        fr: "Conciergeries",
        en: "Concierge services",
        ar: "خدمات الكونسيرج",
      },
      description: {
        fr: "Solution chauffeur privé dédiée aux conciergeries de luxe.",
        en: "Private chauffeur solution dedicated to luxury concierges.",
        ar: "حلّ سائق خاص مخصّص لخدمات الكونسيرج الفاخرة.",
      },
    },
    {
      href: "/chauffeur/partenaires/hotel",
      title: { fr: "Hôtels", en: "Hotels", ar: "الفنادق" },
      description: {
        fr: "Transport premium pour les clients de votre établissement.",
        en: "Premium transport for your hotel's guests.",
        ar: "نقل راقٍ لنزلاء فندقكم.",
      },
    },
    {
      href: "/chauffeur/partenaires/yachting",
      title: { fr: "Yachting", en: "Yachting", ar: "اليخوت" },
      description: {
        fr: "Transferts et mises à disposition pour le secteur du yachting.",
        en: "Transfers and chauffeur hire for the yachting sector.",
        ar: "خدمات النقل والتوفير لقطاع اليخوت.",
      },
    },
    {
      href: "/chauffeur/partenaires/wedding-planner",
      title: {
        fr: "Wedding planners",
        en: "Wedding planners",
        ar: "منظّمو حفلات الزفاف",
      },
      description: {
        fr: "Flotte et chauffeurs pour vos mariages d'exception.",
        en: "Fleet and chauffeurs for your exceptional weddings.",
        ar: "أسطول وسائقون لحفلات زفافكم الاستثنائية.",
      },
    },
    {
      href: "/chauffeur/partenaires/agence-evenementielle",
      title: {
        fr: "Agences événementielles",
        en: "Event agencies",
        ar: "وكالات الفعاليات",
      },
      description: {
        fr: "Logistique de transport pour vos événements.",
        en: "Transport logistics for your events.",
        ar: "الخدمات اللوجستية للنقل في فعالياتكم.",
      },
    },
  ] as NavServiceItem[],
};

export const FOOTER_NAV = {
  columns: {
    chauffeur: {
      fr: "Chauffeur Privé",
      en: "Private Chauffeur",
      ar: "سائق خاص",
    } as T,
    location: { fr: "Location", en: "Rental", ar: "التأجير" } as T,
    partners: {
      fr: "Partenaires & événements",
      en: "Partners & events",
      ar: "الشركاء والمناسبات",
    } as T,
    infos: { fr: "Informations", en: "Information", ar: "معلومات" } as T,
    legal: { fr: "Légal", en: "Legal", ar: "القانونية" } as T,
  },
  intro: {
    fr: "Mobilité haut de gamme sur la Côte d’Azur, Monaco et au‑delà. Chauffeur privé, transferts, événements et location de véhicules d’exception avec une équipe dédiée à l'excellence de service.",
    en: "Premium mobility on the French Riviera, Monaco and beyond. Private chauffeur, transfers, events and exceptional car rental with a team dedicated to service excellence.",
    ar: "تنقّل راقٍ في كوت دازور وموناكو وما وراءهما. سائق خاص ونقل ومناسبات وتأجير سيارات استثنائية مع فريق مكرّس لتميّز الخدمة.",
  } as T,
  cta: {
    rent: {
      fr: "Louer un véhicule",
      en: "Rent a vehicle",
      ar: "استأجر سيارة",
    } as T,
    book: {
      fr: "Réserver un chauffeur",
      en: "Book a chauffeur",
      ar: "احجز سائقًا خاصًا",
    } as T,
  },
  tool: {
    label: {
      fr: "Bon de transport",
      en: "Transport voucher",
      ar: "سند النقل",
    } as T,
  },
  copyright: {
    fr: "Tous droits réservés. · Réalisé et référencé par",
    en: "All rights reserved. · Designed & SEO-optimized by",
    ar: "جميع الحقوق محفوظة. · تصميم وتحسين لمحركات البحث بواسطة",
  } as T,
  chauffeurLinks: [
    {
      href: "/chauffeur",
      label: {
        fr: "Chauffeur privé (hub)",
        en: "Private chauffeur (hub)",
        ar: "السائق الخاص (الصفحة الرئيسية)",
      },
    },
    {
      href: "/chauffeur/services",
      label: {
        fr: "Service Chauffeur Privé",
        en: "Private Chauffeur Service",
        ar: "خدمة السائق الخاص",
      },
    },
    {
      href: "/chauffeur/aeroport",
      label: {
        fr: "Transferts Aéroport",
        en: "Airport Transfers",
        ar: "نقل المطار",
      },
    },
    {
      href: "/chauffeur/transferts",
      label: {
        fr: "Transferts & tarifs (trajets)",
        en: "Transfers & rates (routes)",
        ar: "النقل والأسعار (المسارات)",
      },
    },
    {
      href: "/chauffeur/cannes",
      label: {
        fr: "VTC Cannes",
        en: "Cannes private driver",
        ar: "سائق خاص في كان",
      },
    },
    {
      href: "/chauffeur/nice",
      label: {
        fr: "VTC Nice",
        en: "Nice private driver",
        ar: "سائق خاص في نيس",
      },
    },
    {
      href: "/chauffeur/monaco",
      label: {
        fr: "VTC Monaco",
        en: "Monaco private driver",
        ar: "سائق خاص في موناكو",
      },
    },
    {
      href: "/chauffeur/antibes",
      label: {
        fr: "VTC Antibes",
        en: "Antibes private driver",
        ar: "سائق خاص في أنتيب",
      },
    },
    {
      href: "/chauffeur/saint-tropez",
      label: {
        fr: "VTC Saint-Tropez",
        en: "Saint-Tropez private driver",
        ar: "سائق خاص في سان تروبيه",
      },
    },
    {
      href: "/chauffeur/cote-d-azur",
      label: {
        fr: "Chauffeur Côte d'Azur",
        en: "French Riviera Chauffeur",
        ar: "سائق خاص في كوت دازور",
      },
    },
    {
      href: "/chauffeur/evenements",
      label: { fr: "Événements", en: "Events", ar: "المناسبات" },
    },
    {
      href: "/chauffeur/entreprise",
      label: { fr: "Entreprise", en: "Business", ar: "الشركات" },
    },
  ] as NavLink[],
  partnersLinks: [
    {
      href: "/chauffeur/partenaires",
      label: {
        fr: "Programme partenaires (hub)",
        en: "Partner program (hub)",
        ar: "برنامج الشركاء (الصفحة الرئيسية)",
      },
    },
    {
      href: "/chauffeur/partenaires/conciergerie",
      label: {
        fr: "Partenariat conciergeries",
        en: "Concierge partnership",
        ar: "شراكة خدمات الكونسيرج",
      },
    },
    {
      href: "/chauffeur/partenaires/hotel",
      label: {
        fr: "Partenariat hôtels",
        en: "Hotel partnership",
        ar: "شراكة الفنادق",
      },
    },
    {
      href: "/chauffeur/partenaires/agence-evenementielle",
      label: {
        fr: "Partenariat agences événementielles",
        en: "Event-agency partnership",
        ar: "شراكة وكالات تنظيم المناسبات",
      },
    },
    {
      href: "/chauffeur/partenaires/yachting",
      label: {
        fr: "Partenariat yachting",
        en: "Yachting partnership",
        ar: "شراكة اليخوت",
      },
    },
    {
      href: "/chauffeur/partenaires/wedding-planner",
      label: {
        fr: "Partenariat wedding planners",
        en: "Wedding-planner partnership",
        ar: "شراكة منظّمي حفلات الزفاف",
      },
    },
    {
      href: "/chauffeur/festival-de-cannes",
      label: {
        fr: "Chauffeur Festival de Cannes",
        en: "Cannes Film Festival chauffeur",
        ar: "سائق خاص لمهرجان كان السينمائي",
      },
    },
    {
      href: "/chauffeur/grand-prix-de-monaco",
      label: {
        fr: "Chauffeur Grand Prix de Monaco",
        en: "Monaco Grand Prix chauffeur",
        ar: "سائق خاص لسباق جائزة موناكو الكبرى",
      },
    },
    {
      href: "/chauffeur/cannes-lions",
      label: {
        fr: "Chauffeur Cannes Lions",
        en: "Cannes Lions chauffeur",
        ar: "سائق خاص لمهرجان كان ليونز",
      },
    },
    {
      href: "/chauffeur/monaco-yacht-show",
      label: {
        fr: "Chauffeur Monaco Yacht Show",
        en: "Monaco Yacht Show chauffeur",
        ar: "سائق خاص لمعرض موناكو لليخوت",
      },
    },
  ] as NavLink[],
  locationLinks: [
    {
      href: "/location",
      label: {
        fr: "Location (hub)",
        en: "Rental (hub)",
        ar: "التأجير (الصفحة الرئيسية)",
      },
    },
    {
      href: "/location/vehicules",
      label: { fr: "Véhicules", en: "Vehicles", ar: "السيارات" },
    },
    {
      href: "/location/location-voiture-cannes",
      label: {
        fr: "Location voiture Cannes",
        en: "Car rental Cannes",
        ar: "تأجير سيارات في كان",
      },
    },
    {
      href: "/location/location-voiture-nice",
      label: {
        fr: "Location voiture Nice",
        en: "Car rental Nice",
        ar: "تأجير سيارات في نيس",
      },
    },
    {
      href: "/location/location-voiture-monaco",
      label: {
        fr: "Location voiture Monaco",
        en: "Car rental Monaco",
        ar: "تأجير سيارات في موناكو",
      },
    },
    {
      href: "/location/location-voiture-antibes",
      label: {
        fr: "Location voiture Antibes",
        en: "Car rental Antibes",
        ar: "تأجير سيارات في أنتيب",
      },
    },
    {
      href: "/location/location-voiture-saint-tropez",
      label: {
        fr: "Location voiture Saint-Tropez",
        en: "Car rental Saint-Tropez",
        ar: "تأجير سيارات في سان تروبيه",
      },
    },
    {
      href: "/location/courte-duree",
      label: { fr: "Courte Durée", en: "Short-Term", ar: "التأجير قصير الأمد" },
    },
    {
      href: "/location/avec-chauffeur",
      label: {
        fr: "Avec ou sans chauffeur",
        en: "With or without a driver",
        ar: "مع سائق أو بدونه",
      },
    },
    {
      // Ajoutees le 20/09/2026 avec les pages par carrosserie. Un lien de
      // pied de page site-wide evite la page orpheline : sans lui, seules
      // le sitemap et les fiches vehicule y menent.
      href: "/location/cabriolet",
      label: {
        fr: "Location cabriolet",
        en: "Convertible rental",
        ar: "تأجير سيارة مكشوفة",
      },
    },
    {
      href: "/location/suv",
      label: {
        fr: "Location SUV",
        en: "SUV rental",
        ar: "تأجير دفع رباعي",
      },
    },
    {
      // Les quatre marques a au moins trois vehicules en flotte, ajoutees le
      // 20/09/2026. Meme raison que pour les pages par carrosserie : sans lien
      // au pied de page, une page neuve reste a 3 liens internes quand les
      // autres en ont 242, et ne sort jamais de la page 3.
      href: "/location/mercedes",
      label: {
        fr: "Location Mercedes",
        en: "Mercedes rental",
        ar: "تأجير مرسيدس",
      },
    },
    {
      href: "/location/audi",
      label: { fr: "Location Audi", en: "Audi rental", ar: "تأجير أودي" },
    },
    {
      href: "/location/porsche",
      label: {
        fr: "Location Porsche",
        en: "Porsche rental",
        ar: "تأجير بورشه",
      },
    },
    {
      href: "/location/lamborghini",
      label: {
        fr: "Location Lamborghini",
        en: "Lamborghini rental",
        ar: "تأجير لامبورغيني",
      },
    },
    {
      href: "/location/longue-duree",
      label: { fr: "Longue Durée", en: "Long-Term", ar: "التأجير طويل الأمد" },
    },
    {
      href: "/location/prestige",
      label: { fr: "Prestige", en: "Prestige", ar: "بريستيج" },
    },
    {
      href: "/location/offres",
      label: { fr: "Offres", en: "Offers", ar: "العروض" },
    },
  ] as NavLink[],
  infosLinks: [
    {
      href: "/a-propos",
      label: { fr: "À propos", en: "About", ar: "من نحن" },
    },
    {
      href: "/blog",
      label: { fr: "Blog local", en: "Local blog", ar: "المدوّنة المحلية" },
    },
    {
      href: "/excursions",
      label: {
        fr: "Excursions (hub)",
        en: "Excursions (hub)",
        ar: "الرحلات (الصفحة الرئيسية)",
      },
    },
    {
      href: "/excursions/nice-monaco",
      label: {
        fr: "Excursion Nice Monaco",
        en: "Nice Monaco excursion",
        ar: "رحلة نيس موناكو",
      },
    },
    {
      href: "/excursions/cannes-gourdon",
      label: {
        fr: "Excursion Cannes Gourdon",
        en: "Cannes Gourdon excursion",
        ar: "رحلة كان غوردون",
      },
    },
    {
      href: "/excursions/saint-tropez",
      label: {
        fr: "Excursion Saint-Tropez",
        en: "Saint-Tropez excursion",
        ar: "رحلة سان تروبيه",
      },
    },
    {
      href: "/reservation",
      label: { fr: "Réservation", en: "Booking", ar: "الحجز" },
    },
    {
      href: "/contact",
      label: { fr: "Contact", en: "Contact", ar: "اتصل بنا" },
    },
  ] as NavLink[],
  legalLinks: [
    {
      href: "/mentions-legales",
      label: {
        fr: "Mentions légales",
        en: "Legal notice",
        ar: "البيانات القانونية",
      },
    },
    {
      href: "/cgv",
      label: { fr: "CGV", en: "Terms of Sale", ar: "شروط البيع" },
    },
    {
      href: "/cgu",
      label: { fr: "CGU", en: "Terms of Use", ar: "شروط الاستخدام" },
    },
    {
      href: "/politique-de-confidentialite",
      label: {
        fr: "Politique de confidentialité",
        en: "Privacy Policy",
        ar: "سياسة الخصوصية",
      },
    },
    { href: "/faq", label: { fr: "FAQ", en: "FAQ", ar: "الأسئلة الشائعة" } },
    {
      href: "/cookies",
      label: { fr: "Cookies", en: "Cookies", ar: "ملفات تعريف الارتباط" },
    },
    {
      href: "/plan-du-site",
      label: { fr: "Plan du site", en: "Sitemap", ar: "خريطة الموقع" },
    },
  ] as NavLink[],
};
